import Axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { ActiveProductCatalogue } from "../model/ActiveProductCatalogue";
import { B2bCompany, B2bCompanyCreate } from "../model/B2bCompany";
import { Campaign, CampaignType } from "../model/Campaign";
import { Club } from "../model/Club";
import { Code } from "../model/Code";
import { CodeHolder } from "../model/CodeHolder";
import { CronJob } from "../model/CronJob";
import { CustomField } from "../model/CustomField";
import { MismatchingOrderPaymentSum } from "../model/DashboardReports/MismatchingOrderPaymentSum";
import { DiplomaConfig } from "../model/DiplomaConfig";
import { CompanyEntry, Entry, IAdminCompanyEntryResponse, IAdminSaveCompanyEntryRequest, IListEntriesResponse, ListEntryTransfer, PersonEntry } from "../model/Entry";
import { EntryCodePreview } from "../model/EntryCodePreview";
import { EntryHistoryItem } from "../model/EntryHistoryItem";
import { EntryStatistics } from "../model/EntryStatistics";
import { Event, IEventWithProducts } from "../model/Event";
import { Offer } from "../model/Offer";
import { ExternalSeedingRace } from "../model/ExternalSeedingRace";
import { ExternalSeedingResult } from "../model/ExternalSeedingResult";
import { IExportExcelRequest } from "../model/IExportExcelRequest";
import { Message } from "../model/Message";
import { AddOnSummaryItem, DiplomaOrderItem, EnervitOrderItem, FolksamOrderItem, ListOrder, Order } from "../model/Order";
import { OrderContact } from "../model/OrderContact";
import { OrderItem } from "../model/OrderItem";
import { OrderStatistics } from "../model/OrderStatistics";
import { SportsProSyncStatus } from "../model/SportsProSyncStatus";
import { IceOverview } from "../model/IceOverview";
import { Orderoverview } from "../model/Orderoverview";
import { OrderPayment } from "../model/Payment";
import { IListPersonsResponse, Person } from "../model/Person";
import { PersonResult } from "../model/PersonResult";
import { B2bFakturaName, BeloppsregleringName, KortterminalVlName, PointOfSale } from "../model/PointOfSale";
import { Postcode } from "../model/Postcode";
import { Product, ProductType } from "../model/Product";
import { IAddOrUpdateProductBundleResponse, IGetProductBundleResponse, IListProductBundlesResponse, ProductBundle } from "../model/ProductBundle";
import { ProductCatalogue } from "../model/ProductCatalogue";
import { ProductToDeliver } from "../model/ProductToDeliver";
import { Profile } from "../model/Profile";
import { RaceEntryOverview, RaceEntryOverviewStartGroupDetails } from "../model/RaceEntryOverview";
import { ReportTrippel } from "../model/ReportTrippelParticipants";
import { Reservation } from "../model/Reservation";
import { SeedingTable } from "../model/SeedingTable";
import { IUpdateSupportTicketRequest, SupportTicket, SupportTicketStatus, SupportTicketType } from "../model/SupportTicket";
import { TransferToken } from "../model/TransferToken";
import Firebase from "./Firebase/firebase";
import { ListInvoicesParameters, SetInvoiceStatusRequest, ListCodeHoldersParameters, ListEntriesParameters, ListEntryTransfersParameters, ListOrdersParameters, ListPersonsParameters, RebookOrderItemRequest, IValidateCodeResponse, ListB2bCompaniesParameters, BulkPatchEntriesRequest, TransferEnervitRequest, ListPickupLocationsParameters, ListPickupITemsParameters, IBulkPatchPickupItemsRequest, IPatchPickupItemRequest, ListEngravingEntriesParameters, IListCompanyReservationsResponse, ICreateCompanyReservationRequest, IUpdateCompanyReservationRequest, IListCompanyCodesResponse } from "./models";
import { IInvoice } from "../model/Invoice";
import { RaceType } from "../model/ProductVariantRaceData";
import { IResponseBase } from "../model/IResponseBase";
import { IAddOnOrderItem, IGetAddOnOrderItemsResponse } from "../model/IAddOnOrderItem";
import { AccountingAccountItem, IGetAccountingAccountsResponse } from "../model/IGetAccountingAccountsResponse";
import { IGetOrderTransactionRowsResponse, OrderTransactionRow } from "../model/OrderTransactionRow";
import { IAdminPickupItemResponse, IAdminPickupLocationResponse, ListPickupItemsResponse, ListPickupLocationsResponse, PickupItemLocation } from "../model/PickupItemLocation";
import { PickupItem } from "../model/PickupItem";
import { IListEngravingEntriesResponse } from "../model/ListEngravingEntry";
import { IListCustomSettingsResponse, CustomSetting } from "../model/CustomSetting";
import { IceAdmin } from "../model/IceAdmin";
import { Counter, CounterSetupRow } from "../model/Counter";
import { POSSetting } from "../model/POSSetting";
import { Sex } from "../model/CommonTypes";
import { ImportEntry } from '../model/ImportEntry';
import { FisCode } from "../model/FisCode";

const buildUrl = (entryPath: string) => {
    return `${ApiBackend.config.backendBaseUrl}${entryPath}`;
};

const buildConfig = (token: string): AxiosRequestConfig => {
    return {
        headers: {
            Authorization: token
        }
    };
};

export class ApiBackend {
    static config: config = CONFIG;
    static firebase: Firebase;
    static arraySeparator = "::";

    static async initialize(firebase: Firebase) {
        ApiBackend.firebase = firebase;
    }

    static getCurrentUserName() {
        return ApiBackend.firebase.auth().currentUser.displayName;
    }

    static readonly productTypes: ReadonlyArray<{ name: string, code: ProductType }> = [
        { name: 'Annat', code: 'other' },
        { name: 'Bussbiljett', code: 'busticket' },
        { name: 'Diplom', code: 'diploma' },
        { name: 'Event', code: 'event' },
        { name: 'Lopp', code: 'race' },
        { name: 'Massage', code: 'massage' },
        { name: 'Motionsförsäkring', code: 'insurance' },
        { name: 'Presentkort', code: 'giftcard' },
        { name: 'Spårkort', code: 'skipass' },
        { name: 'Vallaservice', code: 'skiwax' }
    ];

    static readonly raceTypes: ReadonlyArray<{ name: string, code: RaceType }> = [
        { name: 'Individuell', code: 'individual' },
        { name: 'Stafett', code: 'relay' },
        { name: 'Team', code: 'team' }
    ];

    static readonly campaignTypes: ReadonlyArray<{ name: string, code: string }> = [
        { name: 'Vid köp av produkt', code: CampaignType.Code4Product }
    ];

    static getProductQrCodeUrl(productid: string): string {
        return ApiBackend.config.backendBaseUrl + "binary/products/" + productid + "/qrcode";
    }
    static getStartCertificateQrCodeUrl(entryId: string): string {
        return ApiBackend.config.backendBaseUrl + "binary/entries/" + entryId + "/startcertificateqrcode";
    }

    static getPdfReceiptUrl(orderid: string): string {
        return ApiBackend.config.backendBaseUrl + "binary/orders/" + orderid + "/pdf";
    }
    static getStartCertificatePdfUrl(entryid: string): string {
        return ApiBackend.config.backendBaseUrl + "binary/entries/" + entryid + "/startcertificatepdf";
    }

    static buildQueryString<T>(obj: T): string {
        const params = new URLSearchParams();

        Object.keys(obj).forEach((key) => {
            const value = obj[key as keyof T];
            if (Array.isArray(value)) {
                const val = value.join(ApiBackend.arraySeparator);
                if (val) {
                    params.append(key, val)
                }
            } else if (value !== null && value !== undefined && value !== "") {
                params.append(key, value.toString())
            }
        })

        return params.toString();
    }

    downloadAccountingXlsx(year: number, month: number, exportVerDate: string, exportRowComment: string, pointOfSale: string): Promise<void> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "binary/accounting?year=" + year + "&month=" + month + "&verdate=" + exportVerDate + "&rowcomment=" + exportRowComment + "&pointofsale=" + pointOfSale,
                {
                    responseType: 'arraybuffer',
                    headers: {
                        Authorization: token
                    }
                })
                .then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    let y = year + "";
                    let m = month + "";
                    if (+month < 9) {
                        y = (+year - 1) + "";
                        m = (+month + 4) + "";
                    }
                    else {
                        m = (+month - 8) + "";
                    }
                    link.setAttribute('download', 'accounting_kontrollen_' + y + '_' + m + '.xlsx');
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => console.log(error));
        });
    }

    downloadAccountingDailyReport(date: string): Promise<void> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "binary/accountingdaily?date=" + date,
                {
                    responseType: 'arraybuffer',
                    headers: {
                        Authorization: token
                    }
                })
                .then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'kassarapport_vl_' + date + '.xlsx');
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => console.log(error));
        });
    }

    getProfile(): Promise<Profile> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "account", {
                headers: {
                    Authorization: token
                }
            }).then((resp) => {
                let profile = new Profile(resp.data);
                return profile;
            });
        }
        ).catch((err) => {
            return null as Profile;
        });
    }

    updateProfileRoles(userId: string, roles: string[]): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "account/" + userId + "/roles", roles, {
                headers: {
                    Authorization: token
                }
            }).then((resp) => {
                return resp.data;
            });
        }
        ).catch((err) => {
            return null as boolean;
        });
    }

    updateProfile(profile: Profile): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "account", profile,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return false;
                });
        });
    }

    static listAllProfilesCancelToken = Axios.CancelToken.source();
    listAllProfiles(search: string): Promise<Profile[]> {
        if (ApiBackend.listAllProfilesCancelToken) {
            ApiBackend.listAllProfilesCancelToken.cancel();
        }

        ApiBackend.listAllProfilesCancelToken = Axios.CancelToken.source();

        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            const paramFilter = {
                s: search
            };
            const params = ApiBackend.buildQueryString<typeof paramFilter>(paramFilter);

            return Axios.get(ApiBackend.config.backendBaseUrl + "account/accounts?" + params,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return false;
                });
        });
    }

    static listCodesCancelToken = Axios.CancelToken.source();
    listCodes(parameters: ListCodeHoldersParameters): Promise<CodeHolder[]> {
        if (ApiBackend.listCodesCancelToken) {
            ApiBackend.listCodesCancelToken.cancel();
        }

        ApiBackend.listCodesCancelToken = Axios.CancelToken.source();

        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            const params = ApiBackend.buildQueryString<ListCodeHoldersParameters>(parameters);
            const url = ApiBackend.config.backendBaseUrl + "codes?" + params;

            return Axios.get(url, {
                headers: {
                    Authorization: token
                },
                cancelToken: ApiBackend.listCodesCancelToken.token
            }).then((res) => {
                return res.data;
            }).catch((err) => {
                return null as any;
            });
        });
    }

    listCodeHolderCategories(): Promise<string[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "codes/categories",
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return [] as string[];
            });
        });
    }

    async listCodeHolderOrderedBy(): Promise<string[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(
                buildUrl("codes/orderedby"),
                buildConfig(token)
            );
            return response.data ?? [];
        } catch (e) {
            return [];
        }
    }

    async listCodeHolderCodeOwners(): Promise<string[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(
                buildUrl("codes/codeowners"),
                buildConfig(token)
            );
            return response.data ?? [];
        } catch (e) {
            return [];
        }
    }

    listCompanyCategories(): Promise<string[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "b2b/companies/categories",
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return [] as string[];
            });
        });
    }

    listClubs(includeArchived: boolean): Promise<Club[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "entries/clubs" + (includeArchived ? "?includeArchived=true" : ""),
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return null as Club[];
            });
        });
    }

    listFisCodes(): Promise<FisCode[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "entries/fiscodes",
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return null as FisCode[];
            });
        });
    }
    importFisCodes(fisCodes: FisCode[]): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/fiscodes",fisCodes,
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }
    addFisCode(fisCode: FisCode): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "entries/fiscodes",fisCode,
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }


    saveClub(club: Club): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/clubs/" + club.id,
                club,
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return null as boolean;
            });
        });
    }

    getCode(codeHolderId: string): Promise<CodeHolder> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "codes/" + codeHolderId,
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return null as CodeHolder;
            });
        });
    }

    listEvents(): Promise<Event[]> {
        return Axios.get(ApiBackend.config.backendBaseUrl + "events",
        ).then((res) => {
            return res.data;
        }).catch((err) => {
            return null as Event[];
        });
    }

    // ---- Erbjudanden (Offers) ---- (ligger i products-tjänsten: products/offers)
    listOffers(includeArchived: boolean = false): Promise<Offer[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/offers?includeArchived=" + (includeArchived ? "true" : "false"),
                { headers: { Authorization: token } })
                .then((res) => res.data as Offer[])
                .catch((): Offer[] => null);
        });
    }

    getOffer(id: string): Promise<Offer> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/offers/" + id,
                { headers: { Authorization: token } })
                .then((res) => res.data as Offer)
                .catch((): Offer => null);
        });
    }

    createOrUpdateOffer(offer: Offer): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "products/offers", offer,
                { headers: { Authorization: token } })
                .then(() => true)
                .catch(() => false);
        });
    }

    deleteOffer(id: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "products/offers/" + id,
                { headers: { Authorization: token } })
                .then(() => true)
                .catch(() => false);
        });
    }

    // Distinkta län/kommuner/orter (SE) för geografiska filterlistor.
    getGeoData(): Promise<{ counties: string[]; municipalities: string[]; localities: string[] }> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "entries/geodata",
                { headers: { Authorization: token } })
                .then((res) => res.data)
                .catch(() => { return { counties: [], municipalities: [], localities: [] }; });
        });
    }

    listDiplomas(): Promise<DiplomaConfig[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/diplomas",
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data.diplomaConfigs;
            }).catch((err) => {
                return null as DiplomaConfig[];
            });
        });
    }

    addDiplomaConfig(config: DiplomaConfig): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "products/diplomas", config,
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return null as boolean;
            });
        });
    }

    copyDiplomaConfigsYear(sourceYear: number, targetYear: number): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "products/diplomas/copyyear",
                { sourceYear, targetYear },
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                return res.data?.result === true;
            }).catch((err) => {
                console.log(err);
                return false;
            });
        });
    }

    static listOrdersCancelToken = Axios.CancelToken.source();
    listOrders(parameters: ListOrdersParameters): Promise<ListOrder[]> {
        if (ApiBackend.listOrdersCancelToken != null) {
            ApiBackend.listOrdersCancelToken.cancel();
        }

        ApiBackend.listOrdersCancelToken = Axios.CancelToken.source();

        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {

            const params = ApiBackend.buildQueryString<ListOrdersParameters>(parameters);

            return Axios.get(ApiBackend.config.backendBaseUrl + "orders?" + params,
                {
                    cancelToken: ApiBackend.listOrdersCancelToken.token,
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    ApiBackend.listOrdersCancelToken = null;
                    return res.data;
                }).catch((err) => {
                    ApiBackend.listOrdersCancelToken = null;
                    return null as ListOrder[];
                });
        });
    }

    static listInvoicesCancelToken = Axios.CancelToken.source();
    listInvoices(filter: ListInvoicesParameters): Promise<IInvoice[]> {
        if (ApiBackend.listInvoicesCancelToken != null) {
            ApiBackend.listInvoicesCancelToken.cancel();
        }

        ApiBackend.listInvoicesCancelToken = Axios.CancelToken.source();

        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {

            const params = ApiBackend.buildQueryString<ListInvoicesParameters>(filter);

            return Axios.get(ApiBackend.config.backendBaseUrl + "orders/invoices?" + params,
                {
                    cancelToken: ApiBackend.listInvoicesCancelToken.token,
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    ApiBackend.listInvoicesCancelToken = null;
                    return res.data;
                });
        });
    }

    setInvoiceStatus(
        payments: { id: number; externalRef: string; }[],
        codeHolders: { id: string; externalRef: string; }[],
        b2bCustomProducts: { id: string; externalRef: string; }[],
        invoiceStatus: "CREATED"): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            const data: SetInvoiceStatusRequest = {
                payments: payments,
                codeHolders: codeHolders,
                b2bCustomProducts: b2bCustomProducts,
                invoiceStatus: invoiceStatus,
                issuedBy: ApiBackend.getCurrentUserName(),
                issuedWhen: new Date()
            };

            return Axios.post(ApiBackend.config.backendBaseUrl + "orders/invoices/invoicestatus", data,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data?.status === "OK";
                }).catch((err) => {
                    return false;
                });
        });
    }

    listDiplomaOrders(): Promise<DiplomaOrderItem[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + 'orders/reports/diplomaorders',
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data.orders;
                }).catch((err) => {
                    return null as DiplomaOrderItem[];
                });
        });
    }

    listFolksamOrders(fromDate: string, toDate: string): Promise<FolksamOrderItem[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + 'orders/reports/folksamorders?fromDate=' + fromDate + "&toDate=" + toDate,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data.orders;
                }).catch((err) => {
                    return null as FolksamOrderItem[];
                });
        });
    }

    listTrippelParticipants(): Promise<ReportTrippel> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + 'orders/reports/trippelreport',
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as ReportTrippel;
                });
        });
    }

    listFolksamSummary(fromDate: string, toDate: string): Promise<AddOnSummaryItem[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + 'orders/reports/folksamsummary?fromDate=' + fromDate + "&toDate=" + toDate,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data.orders;
                }).catch((err) => {
                    return null as AddOnSummaryItem[];
                });
        });
    }

    listTShirtSummary(raceId: string): Promise<AddOnSummaryItem[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + 'orders/reports/tshirtsummary?raceId=' + raceId,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data.orders;
                }).catch((err) => {
                    return null as AddOnSummaryItem[];
                });
        });
    }

    listEnervitOrders(raceProductId: string, fromDate: string, toDate: string): Promise<EnervitOrderItem[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + 'orders/reports/enervitorders?raceProduct=' + raceProductId + "&fromDate=" + fromDate + "&toDate=" + toDate,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data.orders;
                }).catch((err) => {
                    return null as EnervitOrderItem[];
                });
        });
    }

    listEnervitSummary(fromDate: string, toDate: string): Promise<AddOnSummaryItem[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + 'orders/reports/enervitsummary?fromDate=' + fromDate + "&toDate=" + toDate,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data.orders;
                }).catch((err) => {
                    return null as AddOnSummaryItem[];
                });
        });
    }

    async listEnervitOverview(eventId: string): Promise<AddOnSummaryItem[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(
                buildUrl(`orders/reports/enervitoverview?eventId=${eventId}`),
                buildConfig(token)
            );
            return response.data.orders;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    addOrderPayment(payment: { orderId: string, paymentType: "PAYMENT" | "REVERSAL", amount: number, pointOfSaleId: string, description: string }): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "orders/" + payment.orderId + "/payments", payment,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as boolean;
                });
        });
    }

    sendOrderPaymentRequest(orderId: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "orders/" + orderId + "/sendOrderPaymentLink", {},
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as boolean;
                });
        });
    }

    listPointOfSales(): Promise<PointOfSale[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "orders/pos",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as PointOfSale[];
                });
        });
    }

    getBeloppsregleringPointOfSale(): Promise<PointOfSale> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get<PointOfSale[]>(ApiBackend.config.backendBaseUrl + "orders/pos",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data.find(x => x.name === BeloppsregleringName);
                }).catch((err) => {
                    return null as PointOfSale;
                });
        });
    }

    getKortterminalVlPointOfSale(): Promise<PointOfSale> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get<PointOfSale[]>(ApiBackend.config.backendBaseUrl + "orders/pos",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data.find(x => x.name === KortterminalVlName);
                }).catch((err) => {
                    return null as PointOfSale;
                });
        });
    }

    getB2bFakturaPointOfSale(): Promise<PointOfSale> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get<PointOfSale[]>(ApiBackend.config.backendBaseUrl + "orders/pos",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data.find(x => x.name === B2bFakturaName);
                }).catch((err) => {
                    return null as PointOfSale;
                });
        });
    }

    addOrUpdatePointOfSales(pos: PointOfSale): Promise<PointOfSale> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "orders/pos", pos,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as PointOfSale;
                });
        });
    }

    deletePointOfSale(pos: PointOfSale): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "orders/pos/" + pos.id,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as boolean;
                });
        });
    }

    listProductsToDeliver(filter: string = null, productId: string, productVariantIds: string[], includeDelivered: boolean): Promise<ProductToDeliver[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            const paramFilter = {
                productId: productId,
                productVariantIds: productVariantIds.join(','),
                s: filter,
                includeDelivered: (includeDelivered ? "true" : "false")
            };
            const params = ApiBackend.buildQueryString<typeof paramFilter>(paramFilter);

            return Axios.get(ApiBackend.config.backendBaseUrl + "orders/deliver?" + params,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as ProductToDeliver[];
                });
        });
    }

    deliverProduct(orderId: string, productId: string, productVariantId: string, productVariantPriceGroupId: string, numItems: number): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "orders/deliver", { orderId: orderId, productId: productId, productVariantId: productVariantId, productVariantPriceGroupId: productVariantPriceGroupId, numDelivered: numItems },
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as boolean;
                });
        });
    }

    // ---- Mobil incheckning (/mobilepos) ----

    async listPOSSettings(): Promise<POSSetting[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const res = await Axios.get<POSSetting[]>(ApiBackend.config.backendBaseUrl + "pos", { headers: { Authorization: token } });
            return res.data ?? [];
        } catch (e) {
            console.log(e);
            return [];
        }
    }

    async retrievePublicOrder(publicId: string): Promise<any | null> {
        try {
            const res = await Axios.get(ApiBackend.config.backendBaseUrl + "checkout/order/" + encodeURIComponent(publicId));
            return (res.data && res.data.order) ? res.data.order : null;
        } catch (e) {
            return null;
        }
    }

    async getOrderDeliveries(orderId: string): Promise<any[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const res = await Axios.get(ApiBackend.config.backendBaseUrl + "orders/" + orderId + "/deliveries", { headers: { Authorization: token } });
            return (res.data && res.data.items) ? res.data.items : [];
        } catch (e) {
            return [];
        }
    }

    async deliverCheckinProduct(orderId: string, productId: string, productVariantId: string, productVariantPriceGroupId: string, productVariantOccationId: string, numItems: number, posId: string): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const body: any = {
                orderId, productId, productVariantId, productVariantPriceGroupId,
                numDelivered: numItems, machine: "mobilepos", posId
            };
            if (productVariantOccationId) {
                body.productVariantOccationId = productVariantOccationId;
            }
            const res = await Axios.put(ApiBackend.config.backendBaseUrl + "orders/deliver", body, { headers: { Authorization: token } });
            return res.data === true;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    deliverOrderItem(orderId: string, orderItemId: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "orders/deliver", { orderId: orderId, orderItemId: orderItemId },
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as boolean;
                });
        });
    }

    createOrder(order: Order): Promise<Order> {
        return Axios.put(ApiBackend.config.backendBaseUrl + "checkout/order", order).then((res: { data: { order: Order } }) => {
            if (res && res.data) {
                return res.data.order;
            }
            else {
                return null;
            }
        }).catch((err) => {
            console.log(err);
            return null as Order;
        });

    }

    adminCreateOrder(order: Order, suppressEmail?: boolean): Promise<Order> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "checkout/admin/order", { ...order, ...{ suppressEmail: suppressEmail } },
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                if (res && res.data) {
                    return res.data.order;
                } else {
                    return null;
                }
            }).catch((err) => {
                return null as Order;
            });
        });
    }

    createB2bInvoiceOrder(order: Order): Promise<Order> {
        return Axios.put(ApiBackend.config.backendBaseUrl + "b2b/invoiceorder", order).then((res: { data: { order: Order } }) => {
            if (res && res.data) {
                return res.data.order;
            } else {
                return null;
            }
        }).catch((err) => {
            return null as Order;
        });
    }

    retreiveOrderByPublicId(id: string): Promise<Order> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "orders/publicid/" + id,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as Order;
                });
        });
    }

    retreiveOrder(id: string): Promise<Order> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "orders/" + id,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data.order;
                }).catch((err) => {
                    return null as Order;
                });
        });
    }

    getOrderCommunicationHistory(orderid: string): Promise<Message[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "orders/" + orderid + "/messages",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as Message[];
                });
        });
    }

    getOrderPaymentHistory(orderid: string): Promise<OrderPayment[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "orders/" + orderid + "/payments",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as OrderPayment[];
                });
        });
    }
    verifyPaymentOfOrder(orderid: string): Promise<void> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "payment/status/" + orderid,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as any;
                });
        });
    }

    updateOrderContact(orderid: string, contact: OrderContact): Promise<Order> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "orders/" + orderid + "/contact", contact,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as Order;
                });
        });
    }


    addOrderItems(orderid: string, items: OrderItem[]): Promise<Order> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "orders/" + orderid + "/addOrderItems", { orderItems: items },
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as Order;
                });
        });
    }

    updateOrderItems(orderid: string, items: OrderItem[]): Promise<Order> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "orders/" + orderid + "/items", items,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as Order;
                });
        });
    }

    revertOrderItems(orderid: string, items: OrderItem[], revertedBy: string, revertedReason: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "orders/" + orderid + "/revertItems", { orderItemIds: items.map(x => x.Id), revertedBy: revertedBy, revertedReason: revertedReason },
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as boolean;
                });
        });
    }

    rebookOrderItems(orderId: string, request: RebookOrderItemRequest): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "orders/" + orderId + "/rebookItems", request, {
                headers: {
                    Authorization: token
                }
            }).then((res) => {
                return res.data;
            }).catch((err) => {
                return null as boolean;
            });
        });
    }

    getOrderOverview(productId: string, fromDate?: string, toDate?: string): Promise<Orderoverview> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            const paramFilter = {
                fromDate: fromDate,
                toDate: toDate
            };
            const params = ApiBackend.buildQueryString<typeof paramFilter>(paramFilter);

            return Axios.get(ApiBackend.config.backendBaseUrl + `orders/overview/${productId}?${params}`, {
                headers: {
                    Authorization: token
                }
            }).then((res) => {
                return res.data;
            }).catch((err): Orderoverview => {
                return null;
            });
        });
    }

    static listEntriesCancelToken = Axios.CancelToken.source();
    listEntries(parameters: ListEntriesParameters): Promise<IListEntriesResponse | null> {

        if (ApiBackend.listEntriesCancelToken) {
            ApiBackend.listEntriesCancelToken.cancel();
        }

        ApiBackend.listEntriesCancelToken = Axios.CancelToken.source();

        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            const params = ApiBackend.buildQueryString<ListEntriesParameters>(parameters);

            return Axios.get(ApiBackend.config.backendBaseUrl + "entries?" + params, {
                headers: {
                    Authorization: token
                },
                cancelToken: ApiBackend.listEntriesCancelToken.token
            }).then((res) => {
                return res.data;
            }).catch((err): IListEntriesResponse => {
                return null;
            });
        });
    }

    downloadEntriesAsCSV(eventId: string): Promise<void> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "binary/events/" + eventId + "/entries", {},
                {
                    responseType: 'arraybuffer',
                    headers: {
                        Authorization: token
                    }
                })
                .then((response) => {
                    console.log(response.data);
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'entries_' + eventId + '_' + (new Date().toISOString()) + '.csv');
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => console.log(error));
        });

    }

    static listPersonsCancelToken = Axios.CancelToken.source();
    searchPersons(parameters: ListPersonsParameters): Promise<IListPersonsResponse | null> {
        if (ApiBackend.listPersonsCancelToken) {
            ApiBackend.listPersonsCancelToken.cancel();
        }

        ApiBackend.listPersonsCancelToken = Axios.CancelToken.source();
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            const params = ApiBackend.buildQueryString<ListPersonsParameters>(parameters);

            return Axios.get(ApiBackend.config.backendBaseUrl + "entries/persons?" + params,
                {
                    headers: {
                        Authorization: token
                    },
                    cancelToken: ApiBackend.listPersonsCancelToken.token
                }).then((res) => {
                    return res.data;
                }).catch((err): IListPersonsResponse => {
                    return null;
                });
        });
    }

    getPerson(id: string): Promise<Person> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "entries/persons/" + id,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): Person => {
                    return null;
                });
        });
    }


    getPersonResults(id: string): Promise<PersonResult[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "entries/persons/" + id + "/results",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res: { data: { personId: string, results: PersonResult[] } }) => {
                    return res.data.results;
                }).catch((err): PersonResult[] => {
                    return null;
                });
        });
    }

    refetchPersonResults(id: string): Promise<{ found: number, refetched: number, failed: number }> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/persons/" + id + "/results/refetch", {},
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res: { data: { found: number, refetched: number, failed: number } }) => {
                    return res.data;
                }).catch((): { found: number, refetched: number, failed: number } => {
                    return null;
                });
        });
    }

    savePerson(person: Person): Promise<Person> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/persons/" + person.id, person,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): Person => {
                    return null;
                });
        });
    }

    mergePerson(personId: string, personIdToMergeTo: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/persons/" + personId + "/merge/" + personIdToMergeTo, {},
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): boolean => {
                    return null;
                });
        });
    }

    addPerson(person: Person): Promise<Person> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "entries/persons/", person,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): Person => {
                    return null;
                });
        });
    }

    listB2bCompanies(parameters: ListB2bCompaniesParameters): Promise<B2bCompany[] | null> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            const params = ApiBackend.buildQueryString<ListB2bCompaniesParameters>(parameters);
            const url = ApiBackend.config.backendBaseUrl + "b2b/companies?" + params;

            return Axios.get(url,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): B2bCompany[] => {
                    return null;
                });
        });
    }

    listCompanyProductEntries(companyId: string, companyProductId: string): Promise<{ customHeaders: { key: string; name: string; }[]; entries: CompanyEntry[] }> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}b2b/companies/${companyId}/entries?companyProductId=${companyProductId}`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return null as null;
                });
        });
    }

    getB2bCompany(id: string): Promise<B2bCompany> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "b2b/companies/" + id,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    if (res && res.data && !res.data.products) {
                        res.data.products = [];
                    }

                    return res.data;
                }).catch((err): B2bCompany => {
                    return null;
                });
        });
    }

    saveB2bCompany(company: B2bCompany): Promise<B2bCompany | string> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "b2b/companies/" + company.id, company,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    if (err.response.status === 400) {
                        return err.response.data;
                    }
                    return null;
                });
        });
    }

    createB2bCompany(values: B2bCompanyCreate): Promise<B2bCompany> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "b2b/companies/", values,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): B2bCompany => {
                    return null;
                });
        });
    }

    async getB2bCompanyEntry(companyId: string, entryId: string, companyProductId: string): Promise<CompanyEntry> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<IAdminCompanyEntryResponse>(
                buildUrl(`b2b/companies/${companyId}/entry/${entryId}/product/${companyProductId}`),
                buildConfig(token)
            );
            return response.data?.entry ?? null;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    async saveB2bCompanyEntry(companyId: string, entryId: string, request: IAdminSaveCompanyEntryRequest): Promise<CompanyEntry> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<IAdminSaveCompanyEntryRequest, AxiosResponse<IAdminCompanyEntryResponse>>(
                buildUrl(`b2b/companies/${companyId}/entry/${entryId}`),
                request,
                buildConfig(token)
            );
            return response.data?.entry ?? null;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    async removeB2bCompanyEntry(companyId: string, entryId: string): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.delete<IResponseBase>(
                buildUrl(`b2b/companies/${companyId}/entry/${entryId}`),
                buildConfig(token)
            );
            return response.data?.status === "OK";
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    getEntryStatistics(): Promise<EntryStatistics> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "statistics/entries",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): EntryStatistics => {
                    return null;
                });
        });
    }

    getOrderStatistics(): Promise<OrderStatistics> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "statistics/orders",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): OrderStatistics => {
                    return null;
                });
        });
    }

    getSportsProSyncStatus(): Promise<SportsProSyncStatus> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "statistics/sportsprosync",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): SportsProSyncStatus => {
                    return null;
                });
        });
    }

    getIceOverview(): Promise<IceOverview> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "statistics/iceoverview",
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((): IceOverview => {
                    return null;
                });
        });
    }

    getEvent(eventId: string): Promise<Event> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "events/" + eventId,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): Event => {
                    return null;
                });
        });
    }

    createOrUpdateEvent(event: IEventWithProducts): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "events", event, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    addCode(codeHolderId: string, code: Code): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "codes/" + codeHolderId + "/codes", code, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    updateCode(codeHolderId: string, code: Code): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "codes/" + codeHolderId + "/codes/" + code.id, code, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteCode(codeHolderId: string, code: Code): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "codes/" + codeHolderId + "/codes/" + code.id, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteEvent(event: Event): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "events/" + event.id, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteDiploma(diploma: DiplomaConfig): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "products/diplomas/" + diploma.id, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    createOrUpdateCode(code: CodeHolder): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "codes/", code, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    generateCodes(
        codeHolderId: string,
        numCodes: number,
        maxActivations: number,
        prefix: string,
        numBlocks: number,
        numChars: number): Promise<Code[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            const data = {
                numCodes: numCodes,
                maxActivations: maxActivations,
                prefix: prefix,
                numBlocks: numBlocks,
                numChars: numChars
            };
            return Axios.post<Code[]>(ApiBackend.config.backendBaseUrl + "codes/" + codeHolderId + "/codes/generate", data, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err): Code[] => {
                return undefined;
            });
        });
    }

    deleteCodeHolder(code: CodeHolder): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "codes/" + code.id, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteProduct(product: Product): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "products/" + product.id, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    getProduct(id: string): Promise<Product> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/" + id, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    getProductsForEvent(eventId: string): Promise<Product[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}events/${eventId}/products`, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    updateAgeClassForParticipants(productId: string, variantId: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(`${ApiBackend.config.backendBaseUrl}products/${productId}/${variantId}/updateageclass`, null, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    getEntryOverview(eventId: string): Promise<RaceEntryOverview> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "entries/overview/" + eventId, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    async getEntryOverviewDetails(productId: string, startGroupId?: string, b2bId?: string): Promise<RaceEntryOverviewStartGroupDetails> {
        try {
            if (!startGroupId) {
                startGroupId = "";
            }

            if (!b2bId) {
                b2bId = "";
            }

            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<RaceEntryOverviewStartGroupDetails>(buildUrl(`entries/overviewdetails/${productId}?startGroupId=${startGroupId}&b2bId=${b2bId}`), buildConfig(token));
            return response.data ?? null;
        } catch (e) {
            return null;
        }
    }

    getEntry(entryId: string): Promise<Entry> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "entries/" + entryId, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    getEntryHistory(entryId: string): Promise<EntryHistoryItem[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "entries/" + entryId + "/history", {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data as EntryHistoryItem[];
            }).catch((err) => {
                return [] as EntryHistoryItem[];
            });
        });
    }

    saveEntry(entry: Entry): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/", entry,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): boolean => {
                    return null;
                });
        });
    }

    setEntryB2b(entryId: string, b2bId?: string, b2bCustomProduct?: string): Promise<boolean> {
        const request = {
            entryId: entryId,
            b2bId: b2bId,
            b2bCustomProduct: b2bCustomProduct
        };
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/" + entryId + "/setEntryB2b", request,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data?.status === "OK";
                }).catch((err) => {
                    return false;
                });
        });
    }

    bulkPatchEntries(patchDocument: BulkPatchEntriesRequest): Promise<IResponseBase | false> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/patch", patchDocument,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                });
        });
    }

    splitTeamEntry(entryId: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/" + entryId + "/splitteam", null,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data?.status === "OK";
                }).catch((err) => {
                    return false;
                });
        });
    }

    listProducts(includeArchived: boolean = false): Promise<Product[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/?showarchived=" + (includeArchived ? "1" : "0"), {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    listSeedingTables(): Promise<SeedingTable[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/seeding/tables", {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    runSeedingCalculationForRaceAndGroups(raceId: string, groups: string[], dryRun: boolean, notifyChanges: boolean): Promise<{ jobid: string }> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "products/seeding/run", {
                race: raceId,
                runForGroups: groups,
                dryRun: dryRun,
                notifyStartGroupChange: notifyChanges
            }, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    getSeedingCalculationForRaceAndGroupsStatus(jobid: string): Promise<{ id: string, status: string, progress: number, request: string, response: string }> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/seeding/run/status/" + jobid, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    listExternalSeedingRaces(year: number): Promise<ExternalSeedingRace[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/seeding/externalraces/" + year, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    addOrUpdateExternalSeedingRace(race: ExternalSeedingRace): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "products/seeding/externalraces", race, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteExternalSeedingRace(year: number, raceId: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "products/seeding/externalraces/" + year + "/" + raceId, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    prepareExternalSeedingResults(year: number, raceId: string, results: ExternalSeedingResult[]): Promise<ExternalSeedingResult[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "products/seeding/externalraces/" + year + "/" + raceId + "/prepare",
                { results: results }, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data.results;
            }).catch((err) => {
                return false;
            });
        });
    }
    prepareEntriesForImport(productId: string, productVariantId: string, entries: ImportEntry[]): Promise<ImportEntry[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/prepareimport",
                { entries, productId, productVariantId }, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data.results;
            }).catch((err) => {
                return false;
            });
        });
    }

    prepareLineUpForImport(productId: string, entries: ImportEntry[]): Promise<ImportEntry[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/preparelineupimport",
                { entries, productId }, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data.results;
            }).catch((err) => {
                return false;
            });
        });
    }

    importEntries(b2bCompany: string, productId: string, productVariantId: string, productVariantPriceGroupId: string, entries: ImportEntry[]): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/import",
                { entries, productId, productVariantId, productVariantPriceGroupId, b2bCompany }, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }
    importLineUp(productId: string, entries: ImportEntry[]): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/importlineup",
                { entries, productId }, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    getExternalSeedingResults(year: number, raceId: string): Promise<ExternalSeedingResult[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/seeding/externalraces/" + year + "/" + raceId + "/results", {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data.results;
            }).catch((err) => {
                return false;
            });
        });
    }

    importExternalSeedingResults(year: number, raceId: string, results: ExternalSeedingResult[]): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "products/seeding/externalraces/" + year + "/" + raceId + "/import",
                { results: results }, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    addOrUpdateSeedingTable(table: SeedingTable) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "products/seeding/tables", table, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteSeedingTable(tableId: string) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "products/seeding/tables/" + tableId, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    listAvailableSeedingRaces(year: number): Promise<string[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/seeding/availableraces/" + year, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    listCustomFields(): Promise<CustomField[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "products/customfields/", {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    addCustomField(field: CustomField): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "products/customfields/", field, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    updateCustomField(field: CustomField): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "products/customfields/" + field.id, field, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteCustomField(fieldId: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "products/customfields/" + fieldId, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    listReservations(includeArchived: boolean = false): Promise<Reservation[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "entries/reservations", {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    createReservation(reservation: Reservation): Promise<Reservation> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "entries/reservations", reservation, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    updateReservation(reservation: Reservation): Promise<Reservation> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "entries/reservations/" + reservation.id, reservation, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteReservation(reservation: Reservation): Promise<Reservation> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "entries/reservations/" + reservation.id, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    createProduct(product: Product): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "products/", product, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    updateProduct(product: Product): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "products/" + product.id, product, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    resendOrderconfirmation(orderId: string) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "orders/" + orderId + "/sendconfirmation", null, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    resendGiftCardEmail(orderId: string) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "orders/" + orderId + "/resendGiftCardEmail", null, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    listCronJobs() {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + "cronjobs", {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    addCronJob(job: CronJob) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + "cronjobs", job, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    updateCronJob(job: CronJob) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "cronjobs/" + job.id, job, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteCronJob(job: CronJob) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + "cronjobs/" + job.id, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    static getPostCodeCancelToken = Axios.CancelToken.source();
    getPostCode(postcode: string): Promise<Postcode> {
        if (ApiBackend.getPostCodeCancelToken) {
            ApiBackend.getPostCodeCancelToken.cancel();
        }

        ApiBackend.getPostCodeCancelToken = Axios.CancelToken.source();
        const url = `${ApiBackend.config.backendBaseUrl}externaldata/postcodes/${postcode}/SE`;
        return Axios.get(url, { cancelToken: ApiBackend.getPostCodeCancelToken.token })
            .then((res) => {
                ApiBackend.getPostCodeCancelToken = null;
                return res.data;
            }).catch((): Postcode => {
                ApiBackend.getPostCodeCancelToken = null;
                return null;
            });
    }

    getGeneral<T extends unknown>(path: string): Promise<(T | false)> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(ApiBackend.config.backendBaseUrl + path, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data as T;
            }).catch((err) => {
                return false;
            });
        });
    }

    postGeneral(path: string, data: any) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + path, data, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    putGeneral(path: string, data: any) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(ApiBackend.config.backendBaseUrl + path, data, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteGeneral(path: string) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(ApiBackend.config.backendBaseUrl + path, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err) => {
                return false;
            });
        });
    }

    listCampaigns(): Promise<Campaign[] | null> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}campaigns`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): Campaign[] => {
                    return null;
                });
        });
    }

    getCampaign(id: string): Promise<Campaign> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}campaigns/${id}`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): Campaign => {
                    return null;
                });
        });
    }

    saveOrUpdateCampaign(campaign: Campaign): Promise<Campaign> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(`${ApiBackend.config.backendBaseUrl}campaigns`, campaign,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): Campaign => {
                    return null;
                });
        });
    }

    addCampaignCodes(campaignId: string, codes: string[]) {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(`${ApiBackend.config.backendBaseUrl}campaigns/${campaignId}/codes`, codes,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err) => {
                    return false;
                });
        });
    }

    async getOrdersWithWrongPaymentSum(): Promise<MismatchingOrderPaymentSum[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/dashboard_orderswrongpaymentsum"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getEntriesWithWrongClub(): Promise<Entry[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_wrongclubreport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getCvtaEntriesWithWrongClub(): Promise<Entry[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_cvtawrongclubreport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getEntryPersonsMissingBirthdate(): Promise<(Pick<Person, "id" | "firstName" | "lastName"> & { eventKeys: string; })[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_missingbdatereport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getEntryPersonsMissingNationality(): Promise<(Pick<Person, "id" | "firstName" | "lastName"> & { eventKeys: string; })[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_missingnationalityreport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getEntryPersonsDuplicateEntries(): Promise<(Pick<Person, "id" | "firstName" | "lastName"> & { eventKey: string; })[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_dupentriesreport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getEntriesWithWrongAge(): Promise<Entry[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_wrongagereport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getEntriesWithWrongSex(): Promise<Entry[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_wrongsexreport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getEntryPersonsMissingVasaId(): Promise<(Pick<Person, "id" | "firstName" | "lastName"> & { eventKeys: string; })[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_missingvasaidreport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getEntryPersonsWithWrongGender(): Promise<(Pick<Person, "id" | "firstName" | "lastName" | "gender"> & { sex: Sex; eventKey: string; })[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_wronggenderreport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getEntriesMissingStartGroup(): Promise<Entry[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_missingstartgroupreport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    async getEntriesMissingBib(): Promise<Entry[] | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get(buildUrl("orders/reports/entry_missingbibreport"), buildConfig(token));

            return response.data ?? [];
        } catch (e) {
            return null;
        }
    }

    getEntriesForPerson(personId: string): Promise<PersonEntry[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}entries/persons/${personId}/entries`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): Entry[] => {
                    return [];
                });
        });
    }

    getOrdersForPerson(personId: string): Promise<ListOrder[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}entries/persons/${personId}/orders`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): ListOrder[] => {
                    return [];
                });
        });
    }

    createTransferTokenForPerson(personId: string, method: string): Promise<TransferToken> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}entries/persons/${personId}/transfertoken/${method}`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): TransferToken => {
                    return null;
                });
        });
    }

    createTransferTokenForCompayAdmin(email: string): Promise<TransferToken> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}entries/companies/transfertoken/${email}`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data;
                }).catch((err): TransferToken => {
                    return null;
                });
        });
    }

    // Asynkron export: starta ett jobb, polla status och ladda ner filen när den är klar.
    // Detta undviker API Gateways 29s-tak som gjorde att stora exporter timade ut.
    async exportExcel(request: IExportExcelRequest, fileName?: string): Promise<void> {
        const saveFileName = !!fileName ? fileName : request.entityType + "_" + (new Date().toISOString()) + '.xlsx';
        const base = ApiBackend.config.backendBaseUrl;
        const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

        const startToken = await ApiBackend.firebase.auth().currentUser.getIdToken();
        const start = await Axios.post(base + "binary/excelexport/start", request, {
            headers: { Authorization: startToken }
        });
        const jobid = start.data?.jobid;
        if (!jobid) {
            throw new Error("Kunde inte starta exporten");
        }

        // Worker-Lambdan har upp till 15 min. Polla var 1.5s (600 * 1.5s = 15 min).
        for (let i = 0; i < 600; i++) {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const st = await Axios.get(base + "binary/excelexport/status/" + jobid, {
                headers: { Authorization: token }
            });
            const data = st.data;
            if (data && data.status === "DONE") {
                const link = document.createElement('a');
                link.href = data.response;
                link.setAttribute('download', saveFileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                return;
            }
            if (data && data.status === "ERROR") {
                throw new Error(data.response || "Exporten misslyckades");
            }
            await delay(1500);
        }
        throw new Error("Exporten tog för lång tid");
    }

    listProductCatalogues(): Promise<ProductCatalogue[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}products/catalogues`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    if (res.data?.status === "OK") {
                        return res.data.catalogues;
                    }
                    return [];
                }).catch((err): ProductCatalogue[] => {
                    return [];
                });
        });
    }

    getProductCatalogue(catalogueId: string): Promise<ProductCatalogue> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}products/catalogues/${catalogueId}`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    if (res.data?.status === "OK") {
                        return res.data.catalogue;
                    }
                    return null;
                }).catch((err): ProductCatalogue => {
                    return null;
                });
        });
    }

    addOrUpdateProductCatalogue(catalogue: ProductCatalogue): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(`${ApiBackend.config.backendBaseUrl}products/catalogues/${catalogue.id}`, catalogue, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data?.status === "OK";
            }).catch((err) => {
                return false;
            });
        });
    }

    deleteProductCatalogue(catalogueId: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.delete(`${ApiBackend.config.backendBaseUrl}products/catalogues/${catalogueId}`, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data?.status === "OK";
            }).catch((err) => {
                return false;
            });
        });
    }

    listActiveCatalogues(): Promise<ActiveProductCatalogue[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}products/activecatalogues`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    if (res.data?.status === "OK") {
                        return res.data.catalogues;
                    }
                    return [];
                }).catch((err): ActiveProductCatalogue[] => {
                    return [];
                });
        });
    }

    addOrUpdateActiveProductCatalogue(catalogue: ActiveProductCatalogue): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(`${ApiBackend.config.backendBaseUrl}products/activecatalogues/${catalogue.catalogueKey}`, catalogue, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data?.status === "OK";
            }).catch((err) => {
                return false;
            });
        });
    }

    async listCustomSettings(): Promise<CustomSetting[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<IListCustomSettingsResponse>(buildUrl("products/customsettings"), buildConfig(token));
            return response.data?.settings ?? [];
        } catch (e) {
            return [];
        }
    }

    async addOrUpdateCustomSetting(setting: CustomSetting): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.put<CustomSetting, AxiosResponse<IResponseBase>>(
                `${ApiBackend.config.backendBaseUrl}products/customsettings/${setting.setting}`,
                setting,
                buildConfig(token)
            );
            return response.data?.status === "OK";
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async listIceAdmins(): Promise<IceAdmin[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<{ status: string; admins: IceAdmin[] }>(buildUrl("products/iceadmins"), buildConfig(token));
            return response.data?.admins ?? [];
        } catch (e) {
            console.log(e);
            return [];
        }
    }

    async addOrUpdateIceAdmin(login: string, loginType: string): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.put<{ loginType: string }, AxiosResponse<IResponseBase>>(
                buildUrl(`products/iceadmins/${encodeURIComponent(login)}`),
                { loginType: loginType },
                buildConfig(token)
            );
            return response.data?.status === "OK";
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async deleteIceAdmin(login: string): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.delete<IResponseBase>(
                buildUrl(`products/iceadmins/${encodeURIComponent(login)}`),
                buildConfig(token)
            );
            return response.data?.status === "OK";
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async listCounters(): Promise<Counter[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<{ status: string; counters: Counter[] }>(buildUrl("products/counters"), buildConfig(token));
            return response.data?.counters ?? [];
        } catch (e) {
            console.log(e);
            return [];
        }
    }

    async addOrUpdateCounter(counter: Counter): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<Counter, AxiosResponse<IResponseBase>>(buildUrl("products/counters"), counter, buildConfig(token));
            return response.data?.status === "OK";
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async deleteCounter(counter_key: string): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<{ counter_key: string }, AxiosResponse<IResponseBase>>(buildUrl("products/counters/delete"), { counter_key: counter_key }, buildConfig(token));
            return response.data?.status === "OK";
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async getCounterSetup(year?: number): Promise<{ setup: CounterSetupRow[]; years: number[] }> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const url = year ? `products/countersetup?year=${year}` : "products/countersetup";
            const response = await Axios.get<{ status: string; setup: CounterSetupRow[]; years: number[] }>(buildUrl(url), buildConfig(token));
            return { setup: response.data?.setup ?? [], years: response.data?.years ?? [] };
        } catch (e) {
            console.log(e);
            return { setup: [], years: [] };
        }
    }

    async addOrUpdateCounterSetup(row: CounterSetupRow): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<CounterSetupRow, AxiosResponse<IResponseBase>>(buildUrl("products/countersetup"), row, buildConfig(token));
            return response.data?.status === "OK";
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async deleteCounterSetup(counter_key: string, year: number, eventKey: string): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<any, AxiosResponse<IResponseBase>>(buildUrl("products/countersetup/delete"), { counter_key, year, eventKey }, buildConfig(token));
            return response.data?.status === "OK";
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async copyCounterSetupYear(sourceYear: number, targetYear: number): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<any, AxiosResponse<IResponseBase>>(buildUrl("products/countersetup/copyyear"), { sourceYear, targetYear }, buildConfig(token));
            return response.data?.status === "OK";
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async validateProductDiscountCode(code: string, productId: string, productVariantId: string): Promise<IValidateCodeResponse | null> {
        const body = {
            productId: productId,
            productVariantId: productVariantId
        };

        return Axios.post(ApiBackend.config.backendBaseUrl + "codes/" + code + "/validateforproduct", body).then((res) => {
            if (res && res.data) {
                return res.data;
            }
            else {
                return null;
            }
        }).catch((err): IValidateCodeResponse => {
            console.log(err);
            return null;
        });
    }

    estimateOrder(order: Order): Promise<{ totalPrice: number, order: Order }> {
        return Axios.post(ApiBackend.config.backendBaseUrl + "checkout/order/estimate", order).then((res) => {
            if (res && res.data) {
                return res.data;
            }
            else {
                return null;
            }
        }).catch((err): null => {
            return null;
        });
    }

    adminEstimateOrder(order: Order): Promise<{ totalPrice: number, order: Order }> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(ApiBackend.config.backendBaseUrl + "checkout/admin/order/estimate", order,
                {
                    headers: {
                        Authorization: token
                    }
                }
            ).then((res) => {
                if (res && res.data) {
                    return res.data;
                } else {
                    return null;
                }
            }).catch((err): null => {
                console.log(err);
                return null;
            });
        });
    }

    getSupportTicket(ticketId: string): Promise<SupportTicket> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.get(`${ApiBackend.config.backendBaseUrl}b2b/tickets/${ticketId}`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    if (res.data?.status === "OK") {
                        return res.data.ticket;
                    }
                    return null;
                }).catch((err): SupportTicket => {
                    return null;
                });
        });
    }

    listSupportTickets(typeFilter?: SupportTicketType[], statusFilter?: SupportTicketStatus[], companyId?: string): Promise<SupportTicket[]> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            const paramFilter = {
                typeFilter: typeFilter,
                statusFilter: statusFilter,
                companyId: companyId,
            };
            const params = ApiBackend.buildQueryString<typeof paramFilter>(paramFilter);

            return Axios.get(`${ApiBackend.config.backendBaseUrl}b2b/tickets?${params}`,
                {
                    headers: {
                        Authorization: token
                    }
                }).then((res) => {
                    return res.data?.status === "OK" ? res.data.tickets : [];
                }).catch((err): SupportTicket[] => {
                    return [];
                });
        });
    }

    updateSupportTicket(id: string, request: IUpdateSupportTicketRequest): Promise<SupportTicket> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.put(`${ApiBackend.config.backendBaseUrl}b2b/tickets/${id}`, request, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data?.status === "OK" ? res.data.ticket : null;
            }).catch((err): SupportTicket => {
                return null;
            });
        });
    }

    initializeSwishPayment(orderId: string): Promise<string> {
        return Axios.get(`${ApiBackend.config.backendBaseUrl}payment/swish/initialize/${orderId}?mobile=false`)
            .then((res) => {
                return res.data?.id;
            }).catch((err): string => {
                return null;
            });
    }

    initializeStripePayment(orderId: string, terminalId: string, pointOfSaleId: string): Promise<string> {
        return Axios.post(`${ApiBackend.config.backendBaseUrl}payment/stripe/intitialize`, {
            orderId: orderId, terminalId: terminalId, pointOfSaleId: pointOfSaleId
        }
        ).then((res) => {
            return res.data?.id;
        }).catch((err): string => {
            return null;
        });
    }

    checkSwishPaymentStatus(refId: string): Promise<"PAID" | "DECLINED" | "ERROR" | ""> {
        return Axios.get(`${ApiBackend.config.backendBaseUrl}payment/swish/status/${refId}`)
            .then((res) => {
                if (!res.data?.status) {
                    return "ERROR";
                }

                return res.data.status;
            }).catch((err) => {
                return "ERROR";
            });
    }

    cancelStripePayment(refId: string): Promise<void> {
        return Axios.get(`${ApiBackend.config.backendBaseUrl}payment/stripe/${refId}/cancel`)
            .then((res) => {
                return;
            }).catch((err) => {
                return;
            });
    }

    checkStripePaymentStatus(refId: string): Promise<"PAID" | "DECLINED" | "ERROR" | ""> {
        return Axios.get(`${ApiBackend.config.backendBaseUrl}payment/stripe/status/${refId}`)
            .then((res) => {
                if (!res.data?.status) {
                    return "ERROR";
                }

                return res.data.status;
            }).catch((err) => {
                return "ERROR";
            });
    }

    resendStartCertificate(entryId: string): Promise<boolean> {
        return ApiBackend.firebase.auth().currentUser.getIdToken().then(token => {
            return Axios.post(`${ApiBackend.config.backendBaseUrl}entries/${entryId}/startcertificate/resend`, {}, {
                headers: {
                    Authorization: token
                }
            }
            ).then((res) => {
                return res.data;
            }).catch((err): boolean => {
                return null;
            });
        });
    }

    async validateEntryForRace(entry: Entry, productId: string, productVariantId: string): Promise<IValidationMessage[] | null> {
        const body = {
            entry: entry,
            productId: productId,
            productVariantId: productVariantId
        };

        return Axios.post<IValidateEntryResponse>(ApiBackend.config.backendBaseUrl + "checkout/entry/validate", body).then((res) => {
            if (res?.data?.hasErrors) {
                return res.data.validationMessages;
            }
            return null;
        }).catch((err): IValidationMessage[] => {
            return null;
        });
    }

    async listProductBundles(): Promise<ProductBundle[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<IListProductBundlesResponse>(buildUrl("products/bundles"), buildConfig(token));
            return response.data?.bundles ?? [];
        } catch (e) {
            return [];
        }
    }

    async getProductBundle(bundleId: string): Promise<ProductBundle> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<IGetProductBundleResponse>(buildUrl(`products/bundles/${bundleId}`), buildConfig(token));
            return response.data?.bundle ?? null;
        } catch (e) {
            return null;
        }
    }

    async addOrUpdateProductBundle(bundle: ProductBundle): Promise<ProductBundle> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.put<IAddOrUpdateProductBundleResponse>(
                buildUrl(`products/bundles/${bundle.id}`),
                bundle,
                buildConfig(token)
            );
            return response.data?.bundle ?? null;
        } catch (e) {
            return null;
        }
    }

    async getEntryAddonOrderItems(entryId: string): Promise<IAddOnOrderItem[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<IGetAddOnOrderItemsResponse>(buildUrl(`entries/${entryId}/addonorderitems`), buildConfig(token));

            if (response.data?.status === "OK") {
                return response.data.addOns;
            }

            return [];
        } catch (e) {
            return [];
        }
    }

    async createEntryAllowance(entryId: string, pdfSuffix: string, ssn?: string): Promise<boolean> {
        try {
            const config = { responseType: "arraybuffer" } as any;
            const url = `${ApiBackend.config.backendBaseUrl}binary/entries/${entryId}/wellnesspdf`;
            const response = await Axios.post(url, { socialSecurity: ssn }, config);

            const fileName = `friskvårdskvitto_${pdfSuffix}.pdf`;
            const targetUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = targetUrl;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(targetUrl);

            return true;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async getAccountingAccounts(fromDate: string, toDate: string, groupBy: "account" | "product", paymentChannel: string, account: string): Promise<AccountingAccountItem[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<IGetAccountingAccountsResponse>(buildUrl(`orders/accountingaccounts/?fromDate=${fromDate}&toDate=${toDate}&groupBy=${groupBy}&paymentChannel=${paymentChannel ? paymentChannel : ""}&account=${account ? account : ""}`), buildConfig(token));

            if (response.data?.status === "OK") {
                return response.data.accounts;
            }

            return [];
        } catch (e) {
            return [];
        }
    }

    async getOrderTransactionsForAccount(account: string, type: "account" | "product", fromDate: string, toDate: string, paymentChannelFilter: string): Promise<OrderTransactionRow[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<IGetOrderTransactionRowsResponse>(buildUrl(`orders/transactions/?account=${account}&type=${type}&fromDate=${fromDate}&toDate=${toDate}&paymentChannel=${paymentChannelFilter}`), buildConfig(token));

            if (response.data?.status === "OK") {
                return response.data.rows;
            }

            return [];
        } catch (e) {
            return [];
        }
    }

    async transferEnervit(orderId: string, request: TransferEnervitRequest): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<boolean>(
                buildUrl(`orders/${orderId}/transferEnervit`),
                request,
                buildConfig(token)
            );
            return response.data;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async previewEntryCode(orderId: string, request: { orderItemId: string; code: string }): Promise<{ status: string; message?: string; preview?: EntryCodePreview }> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post(
                buildUrl(`orders/${orderId}/previewEntryCode`),
                request,
                buildConfig(token)
            );
            return response.data;
        } catch (e) {
            return { status: "ERROR", message: e?.response?.data?.message ?? "Kunde inte kontrollera koden" };
        }
    }

    async addEntryCode(orderId: string, request: { orderItemId: string; code: string; issuedBy: string }): Promise<{ status: string; message?: string }> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post(
                buildUrl(`orders/${orderId}/addEntryCode`),
                request,
                buildConfig(token)
            );
            return response.data;
        } catch (e) {
            return { status: "ERROR", message: e?.response?.data?.message ?? "Ett fel uppstod när koden skulle läggas till" };
        }
    }

    async removeEntryCode(orderId: string, request: { orderItemId: string; issuedBy: string }): Promise<{ status: string; message?: string }> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post(
                buildUrl(`orders/${orderId}/removeEntryCode`),
                request,
                buildConfig(token)
            );
            return response.data;
        } catch (e) {
            return { status: "ERROR", message: e?.response?.data?.message ?? "Ett fel uppstod när koden skulle tas bort" };
        }
    }

    static listPickupLocationsCancelToken = Axios.CancelToken.source();
    async listPickupLocations(parameters: ListPickupLocationsParameters): Promise<ListPickupLocationsResponse> {
        try {
            if (ApiBackend.listPickupLocationsCancelToken) {
                ApiBackend.listPickupLocationsCancelToken.cancel();
            }

            ApiBackend.listPickupLocationsCancelToken = Axios.CancelToken.source();

            const params = ApiBackend.buildQueryString<ListPickupLocationsParameters>(parameters);
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            let config = buildConfig(token);
            config.cancelToken = ApiBackend.listPickupLocationsCancelToken.token;

            const response = await Axios.get<ListPickupLocationsResponse>(
                buildUrl(`pickup/locations?${params}`),
                config
            );
            return response.data;
        } catch (e) {
            console.log(e);
            return null;
        } finally {
            ApiBackend.listPickupLocationsCancelToken = null;
        }
    }

    async createPickupItemLocation(location: PickupItemLocation): Promise<PickupItemLocation> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<IAdminPickupLocationResponse>(
                buildUrl(`pickup/location`),
                location,
                buildConfig(token)
            );
            return response.data?.location ?? null;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    async updatePickupItemLocation(locationId: string, location: PickupItemLocation): Promise<PickupItemLocation> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<IAdminPickupLocationResponse>(
                buildUrl(`pickup/location/${locationId}`),
                location,
                buildConfig(token)
            );
            return response.data?.location ?? null;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    async getPickupItemLocation(locationId: string): Promise<PickupItemLocation> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<IAdminPickupLocationResponse>(
                buildUrl(`pickup/location/${locationId}`),
                buildConfig(token)
            );
            return response.data?.location ?? null;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    static listPickupItemsCancelToken = Axios.CancelToken.source();
    async listPickupItems(locationId: string, parameters: ListPickupITemsParameters): Promise<ListPickupItemsResponse> {
        try {
            if (ApiBackend.listPickupItemsCancelToken) {
                ApiBackend.listPickupItemsCancelToken.cancel();
            }

            ApiBackend.listPickupItemsCancelToken = Axios.CancelToken.source();

            const params = ApiBackend.buildQueryString<ListPickupITemsParameters>(parameters);
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            let config = buildConfig(token);
            config.cancelToken = ApiBackend.listPickupItemsCancelToken.token;

            const response = await Axios.get<ListPickupItemsResponse>(
                buildUrl(`pickup/location/${locationId}/items?${params}`),
                config
            );
            return response.data;
        } catch (e) {
            console.log(e);
            return null;
        } finally {
            ApiBackend.listPickupItemsCancelToken = null;
        }
    }

    async createPickupItem(locationId: string, item: PickupItem): Promise<PickupItem> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<IAdminPickupItemResponse>(
                buildUrl(`pickup/location/${locationId}/item`),
                item,
                buildConfig(token)
            );

            return response.data?.item ?? null;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    async bulkPatchPickupItems(locationId: string, patchDocument: IBulkPatchPickupItemsRequest): Promise<IResponseBase | false> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<IResponseBase>(
                buildUrl(`pickup/location/${locationId}/items/patch`),
                patchDocument,
                buildConfig(token)
            );

            return response.data;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    async patchPickupItem(locationId: string, itemId: string, patchDocument: IPatchPickupItemRequest): Promise<PickupItem> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<IAdminPickupItemResponse>(
                buildUrl(`pickup/location/${locationId}/item/${itemId}/patch`),
                patchDocument,
                buildConfig(token)
            );
            return response.data?.item ?? null;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    static listEngravingEntriesCancelToken = Axios.CancelToken.source();
    async listEngravingEntries(parameters: ListEngravingEntriesParameters): Promise<IListEngravingEntriesResponse | null> {
        try {
            if (ApiBackend.listEngravingEntriesCancelToken) {
                ApiBackend.listEngravingEntriesCancelToken.cancel();
            }

            ApiBackend.listEngravingEntriesCancelToken = Axios.CancelToken.source();

            const params = ApiBackend.buildQueryString<ListEngravingEntriesParameters>(parameters);
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            let config = buildConfig(token);
            config.cancelToken = ApiBackend.listEngravingEntriesCancelToken.token;

            const response = await Axios.get<IListEngravingEntriesResponse>(
                buildUrl(`entries/engraving?${params}`),
                config
            );
            return response.data;
        } catch (e) {
            console.log(e);
            return null;
        } finally {
            ApiBackend.listEngravingEntriesCancelToken = null;
        }
    }

    async cancelEntryTransfer(entryId: string, entryTransferId: string): Promise<boolean> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.delete<IResponseBase>(
                buildUrl(`entries/${entryId}/entrytransfer/${entryTransferId}`),
                buildConfig(token)
            );
            return response.data?.status === "OK";
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async listEntryTransfers(parameters: ListEntryTransfersParameters): Promise<ListEntryTransfer[]> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const params = ApiBackend.buildQueryString<ListEntryTransfersParameters>(parameters);
            const response = await Axios.get<ListEntryTransfer[]>(
                buildUrl(`entries/entrytransfers?${params}`),
                buildConfig(token)
            );
            return response.data ?? [];
        } catch (e) {
            console.log(e);
            return [];
        }
    }

    async listCompanyReservations(companyId: string): Promise<IListCompanyReservationsResponse | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<IListCompanyReservationsResponse>(buildUrl(`b2b/companies/${companyId}/reservations`), buildConfig(token));

            return response.data;
        } catch (e) {
            return null;
        }
    }

    async listCompanyCodes(companyId: string): Promise<IListCompanyCodesResponse | null> {
        try {
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<IListCompanyCodesResponse>(buildUrl(`b2b/companies/${companyId}/codes`), buildConfig(token));

            return response.data;
        } catch (e) {
            return null;
        }
    }

    async createCompanyReservation(companyId: string, pointOfSale: string, order: Order): Promise<boolean> {
        try {
            const request: ICreateCompanyReservationRequest = {
                pointOfSaleId: pointOfSale,
                order: order
            };
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.post<ICreateCompanyReservationRequest, AxiosResponse<IResponseBase>>(
                buildUrl(`b2b/companies/${companyId}/reservation`),
                request,
                buildConfig(token)
            );
            return response?.data?.status === "OK";
        } catch (e) {
            return false;
        }
    }

    async updateCompanyReservation(companyId: string, orderId: string, numItems: number, reservationName: string): Promise<boolean> {
        try {
            const request: IUpdateCompanyReservationRequest = {
                orderId: orderId,
                numItems: numItems,
                reservationName: reservationName
            };
            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.put<IUpdateCompanyReservationRequest, AxiosResponse<IResponseBase>>(
                buildUrl(`b2b/companies/${companyId}/reservation/${orderId}/update`),
                request,
                buildConfig(token)
            );
            return response?.data?.status === "OK";
        } catch (e) {
            return false;
        }
    }
    async listVeterans(view?: string, jubileeYear?: number, onlyEntered? : boolean): Promise<(Person & { lastClub: string; })[]> {
        try {
            if (!view) {
                view = "veterans";
            }

            const token = await ApiBackend.firebase.auth().currentUser.getIdToken();
            const response = await Axios.get<(Person & { lastClub: string })[]>(
                buildUrl(`entries/veterans?view=${view}&jubileeYear=${jubileeYear}&onlyEntered=${onlyEntered ? 1:0}`), buildConfig(token)
            );
            return response?.data;
        } catch (e) {
            return null;
        }
    }
}

interface config {
    backendBaseUrl: string;
    checkoutUrl: string;
    minaSidorUrl: string;
    b2bUrl: string;
}

interface IValidateEntryResponse {
    status: "OK" | "ERROR";
    hasErrors: boolean;
    validationMessages?: IValidationMessage[];
}

interface IValidationMessage {
    severity: "error"; // TODO? "info" | "warning" | "error";
    code: "duplicate_entry"
}
